import { useState, useRef } from 'react';
import { productCategories, categoryLinks } from '@/mocks/products';

function ProductCard({ product }: { product: { id: string; name: string; image: string; price: string; link: string } }) {
  return (
    <a
      href={product.link}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex-shrink-0 w-[220px] md:w-[260px] flex flex-col bg-white rounded-xl border border-sage-100 overflow-hidden hover:shadow-lg transition"
      data-product-shop
    >
      <div className="relative aspect-square bg-sage-50 overflow-hidden">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-3 md:p-4 flex flex-col flex-1">
        <h3 className="text-sm font-medium text-sage-900 leading-snug line-clamp-2 mb-2 flex-1">
          {product.name}
        </h3>
        <div className="flex items-center justify-between mt-auto">
          <span className="text-sm font-bold text-coral-600">{product.price}</span>
          <span className="text-xs text-sage-500 font-medium opacity-0 group-hover:opacity-100 transition">Buy →</span>
        </div>
      </div>
    </a>
  );
}

function CategoryCarousel({ category }: { category: { name: string; products: { id: string; name: string; image: string; price: string; link: string }[] } }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  };

  const scroll = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = 280;
    el.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' });
    setTimeout(checkScroll, 300);
  };

  return (
    <div className="mb-12 md:mb-16">
      <div className="flex items-center justify-between mb-5 md:mb-6">
        <div className="flex items-center gap-3">
          <h3 className="font-heading text-lg md:text-xl font-bold text-sage-900">{category.name}</h3>
          <span className="text-xs text-sage-500 bg-sage-50 px-2 py-1 rounded-full">{category.products.length} items</span>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={categoryLinks[category.name] || 'https://kaspreadsheet.com'}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs md:text-sm font-medium text-coral-600 hover:text-coral-700 transition mr-3"
          >
            View All
          </a>
          <button
            onClick={() => scroll('left')}
            disabled={!canScrollLeft}
            className="w-8 h-8 rounded-full bg-sage-50 flex items-center justify-center text-sage-600 hover:bg-sage-100 transition disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Previous"
          >
            <i className="ri-arrow-left-s-line w-5 h-5 flex items-center justify-center" />
          </button>
          <button
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
            className="w-8 h-8 rounded-full bg-sage-50 flex items-center justify-center text-sage-600 hover:bg-sage-100 transition disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Next"
          >
            <i className="ri-arrow-right-s-line w-5 h-5 flex items-center justify-center" />
          </button>
        </div>
      </div>
      <div
        ref={scrollRef}
        onScroll={checkScroll}
        className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide scroll-smooth"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {category.products.map(product => (
          <ProductCard key={product.id} product={product} />
        ))}
      </div>
    </div>
  );
}

export default function ProductsSection() {
  return (
    <section id="products" className="py-16 md:py-24 bg-white">
      <div className="w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        <div className="text-center mb-10 md:mb-14">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-sage-900 mb-4">Trending Products</h2>
          <p className="text-sage-600 max-w-2xl mx-auto">
            Browse curated finds from the Kakobuy Spreadsheet — click any item to view details and purchase on the main platform.
          </p>
        </div>
        {productCategories.map(category => (
          <CategoryCarousel key={category.name} category={category} />
        ))}
        <div className="text-center mt-8">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-coral-500 text-white font-semibold text-sm hover:bg-coral-600 transition whitespace-nowrap"
          >
            <i className="ri-external-link-line w-4 h-4 flex items-center justify-center" />
            Browse Full Spreadsheet
          </a>
        </div>
      </div>
    </section>
  );
}